import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";

export default function PoliticaReembolso() {
  return (
    <>
      <SEOHead
        title="Política de Reembolso"
        description="Política de Reembolso do Vendas Trainer. Conheça as condições e prazos para solicitar reembolso dos planos contratados."
        keywords="reembolso, cancelamento, devolução, garantia, política de reembolso"
        canonicalUrl="https://bianchimbusiness.com.br/politica-reembolso"
      />

      <Header />

      <main 
        id="main-content" 
        className="min-h-screen bg-background pt-20"
        role="main"
        aria-labelledby="page-title"
      >
        <article className="container mx-auto px-4 py-16 max-w-4xl">
          <header>
            <h1 
              id="page-title" 
              className="text-4xl font-bold text-foreground mb-8"
            >
              Política de Reembolso
            </h1>
          </header>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-6 text-muted-foreground">
            <p className="text-lg">
              <strong>Última atualização:</strong> Agosto de 2026
            </p>

            <section className="space-y-4" aria-labelledby="intro-section">
              <h2 id="intro-section" className="text-2xl font-semibold text-foreground">
                1. Introdução
              </h2>
              <p>
                A Bianchim Business, responsável pelo Vendas Trainer, valoriza a satisfação de seus clientes. 
                Esta Política de Reembolso estabelece as condições, prazos e procedimentos para solicitar 
                reembolso de pagamentos realizados em nossa plataforma.
              </p>
            </section>

            <section className="space-y-4" aria-labelledby="guarantee-section">
              <h2 id="guarantee-section" className="text-2xl font-semibold text-foreground">
                2. Direito de Arrependimento (7 dias)
              </h2>
              <p>
                Conforme o <strong>art. 49 do Código de Defesa do Consumidor</strong>, compras realizadas fora do
                estabelecimento comercial (internet) podem ser desistidas no prazo de <strong>7 (sete) dias corridos</strong>{" "}
                contados da contratação, com devolução dos valores pagos.
              </p>
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
                <p className="text-foreground font-medium">
                  ✓ Reembolso integral em até 7 dias da compra, desde que você tenha utilizado no máximo 10 feedbacks
                </p>
              </div>
            </section>

            <section className="space-y-4" aria-labelledby="conditions-section">
              <h2 id="conditions-section" className="text-2xl font-semibold text-foreground">
                3. Condições para Reembolso
              </h2>
              <p>O botão de reembolso fica ativo no painel do coordenador (abaixo do plano) quando:</p>
              <ul className="list-disc pl-6 space-y-2" role="list">
                <li>
                  <strong>Prazo:</strong> a compra foi feita há menos de 7 dias
                </li>
                <li>
                  <strong>Uso:</strong> foram consumidos no máximo <strong>10 feedbacks</strong> no ciclo (equivalente ao
                  que já é oferecido gratuitamente no plano Start)
                </li>
                <li>
                  <strong>Pagamento:</strong> a assinatura foi contratada por cartão/checkout online (Stripe)
                </li>
              </ul>
              <p>
                Além disso, seguem elegíveis os casos de <strong>cobrança indevida</strong>, plataforma indisponível por
                mais de 72 horas consecutivas sem justificativa e serviço substancialmente diferente do anunciado.
              </p>
            </section>

            <section className="space-y-4" aria-labelledby="excluded-section">
              <h2 id="excluded-section" className="text-2xl font-semibold text-foreground">
                4. Casos Não Elegíveis para Reembolso
              </h2>
              <p>O reembolso não será concedido nos seguintes casos:</p>
              <ul className="list-disc pl-6 space-y-2" role="list">
                <li>
                  <strong>Prazo excedido:</strong> solicitações após 7 dias corridos da compra — nesse caso o botão fica
                  inativo e o painel exibe a explicação do prazo legal
                </li>
                <li>
                  <strong>Uso do saldo bônus:</strong> mais de 10 feedbacks consumidos no ciclo, pois o saldo gratuito foi
                  esgotado e o benefício pago começou a ser utilizado
                </li>
                <li>Planos ativados por voucher, brinde ou negociação presencial (não houve pagamento online)</li>
                <li>Violação dos Termos de Uso</li>
                <li>Cancelamento automático por falta de pagamento</li>
              </ul>
            </section>

            <section className="space-y-4" aria-labelledby="process-section">
              <h2 id="process-section" className="text-2xl font-semibold text-foreground">
                5. Como Solicitar Reembolso
              </h2>
              <p>
                O processo é <strong>automático e feito por você</strong>, sem necessidade de contato:
              </p>
              <ol className="list-decimal pl-6 space-y-3" role="list">
                <li>
                  <strong>Acesse o painel:</strong> entre com o usuário coordenador e localize o cartão “Plano”
                </li>
                <li>
                  <strong>Clique em “Pedir reembolso”:</strong> o botão aparece ativo apenas se você atende às condições
                  do item 3
                </li>
                <li>
                  <strong>Confirme o alerta:</strong> a ação é irreversível — a assinatura é encerrada na hora e a conta
                  volta ao plano Start
                </li>
                <li>
                  <strong>Processamento:</strong> o estorno é enviado automaticamente à Stripe e processado em até{" "}
                  <strong>10 dias úteis</strong>
                </li>
              </ol>
              <p>
                Em casos de cobrança indevida ou dúvidas, escreva para{" "}
                <a
                  href="mailto:junior@bianchimbusiness.com.br"
                  className="text-primary hover:underline"
                  aria-label="Enviar e-mail para solicitar reembolso"
                >
                  junior@bianchimbusiness.com.br
                </a>
                .
              </p>
            </section>

            <section className="space-y-4" aria-labelledby="payment-section">
              <h2 id="payment-section" className="text-2xl font-semibold text-foreground">
                6. Forma de Reembolso
              </h2>
              <p>O reembolso será realizado da seguinte forma:</p>
              <ul className="list-disc pl-6 space-y-2" role="list">
                <li>
                  <strong>Cartão de crédito:</strong> Estorno na próxima fatura ou crédito em fatura futura 
                  (prazo de até 2 ciclos de faturamento)
                </li>
                <li>
                  <strong>PIX:</strong> Transferência para a chave utilizada no pagamento original ou 
                  conta indicada pelo cliente
                </li>
                <li>
                  <strong>Boleto:</strong> Transferência bancária para conta indicada pelo cliente
                </li>
              </ul>
            </section>

            <section className="space-y-4" aria-labelledby="partial-section">
              <h2 id="partial-section" className="text-2xl font-semibold text-foreground">
                7. Reembolso Parcial (planos anuais)
              </h2>
              <p>
                No cancelamento de um <strong>plano anual</strong>, estornamos automaticamente o valor{" "}
                <strong>proporcional aos meses ainda não utilizados</strong>. O mês em curso é considerado consumido e não
                é estornado. O cálculo e o estorno são feitos pela Stripe, com processamento em até 10 dias úteis.
              </p>
            </section>

            <section className="space-y-4" aria-labelledby="cancellation-section">
              <h2 id="cancellation-section" className="text-2xl font-semibold text-foreground">
                8. Cancelamento de Assinatura
              </h2>
              <p>
                O cancelamento pode ser feito a qualquer momento pelo próprio coordenador, no cartão “Plano” do painel,
                clicando em <strong>“Cancelar plano”</strong>. Ao confirmar o alerta (ação irreversível):
              </p>
              <ul className="list-disc pl-6 space-y-2" role="list">
                <li>
                  <strong>Mensalidade:</strong> o mês em curso já foi pago e permanece disponível até o fim do período;
                  o cancelamento vale a partir do mês seguinte, sem nova cobrança
                </li>
                <li>
                  <strong>Plano anual:</strong> o valor equivalente aos meses restantes é estornado automaticamente
                </li>
                <li>
                  <strong>Plano ativado por voucher:</strong> o cancelamento é automático e a conta volta ao plano Start.
                  Se o voucher foi obtido em pagamento presencial ou direto, procure o fornecedor/consultor responsável
                  pela venda para tratar de reembolso. Se o voucher foi um presente gratuito recebido por e-mail, não há
                  valor a reembolsar
                </li>
                <li>Não haverá cobranças futuras e os dados são mantidos por 30 dias após o fim da assinatura</li>
              </ul>
            </section>

            <section className="space-y-4" aria-labelledby="disputes-section">
              <h2 id="disputes-section" className="text-2xl font-semibold text-foreground">
                9. Disputas e Chargebacks
              </h2>
              <p>
                Antes de abrir uma disputa ou chargeback junto à operadora do cartão, entre em contato 
                conosco. Estamos comprometidos em resolver qualquer questão de forma amigável e rápida. 
                Disputas abertas sem tentativa prévia de contato podem resultar em suspensão da conta.
              </p>
            </section>

            <section className="space-y-4" aria-labelledby="contact-section">
              <h2 id="contact-section" className="text-2xl font-semibold text-foreground">
                10. Contato
              </h2>
              <p>
                Para dúvidas sobre reembolso ou cancelamento:
              </p>
              <address className="not-italic space-y-2">
                <p>
                  <strong>E-mail:</strong>{" "}
                  <a 
                    href="mailto:junior@bianchimbusiness.com.br"
                    className="text-primary hover:underline"
                  >
                    junior@bianchimbusiness.com.br
                  </a>
                </p>
                <p>
                  <strong>Telefone:</strong>{" "}
                  <a 
                    href="tel:+5516981960018"
                    className="text-primary hover:underline"
                  >
                    +55 (16) 98196-0018
                  </a>
                </p>
                <p>
                  <strong>Endereço:</strong> Rua Danilo Micalli, 50 - Santa Felícia, São Carlos – SP – CEP: 13563-264
                </p>
              </address>
            </section>
          </div>
        </article>
      </main>

      <PageNavigation />
      <Footer />
    </>
  );
}
